/**
 * DeepHealth Design Language System — MUI Theme Configuration
 *
 * PortalApi merges server-side ThemePresets into configOverride before the
 * client builds the MUI theme. The local tokens below are only a bootstrap
 * fallback for first render or missing configuration.
 */

import { createTheme, darken, type Theme, type ThemeOptions } from '@mui/material/styles';
import { STYLE_PACKS, type StylePackName, isStylePackName } from './stylePacks';
import { applyStyleRules } from './styleRuleRegistry';

// ─────────────────────────────────────────────────────────────────────────────
// Custom palette keys.
//
// These four tokens travel from the DLS through /Experience but had nowhere to land — they were
// declared in ThemeTokens and THEME_OVERRIDE_KEYS yet referenced by nothing, so a facility could
// author them and see no effect. Augmenting the palette both wires them into the component
// overrides below AND makes them usable from `sx` (theme.palette.rule, 'soft', …) the same way
// primary and divider are, which is what page-level code will reach for.
// ─────────────────────────────────────────────────────────────────────────────
declare module '@mui/material/Divider' {
  interface DividerPropsVariantOverrides {
    /** Stronger section rule, driven by the `rule` token. */
    rule: true;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    /** Stronger than `divider` — section rules, input borders, table cell borders. */
    rule: string;
    /** Tonal wash. Table headers, text-button rest state, inset panels. */
    soft: string;
    /** Dark neutral for inverted surfaces. Tooltips, dark bands. */
    carbon: string;
    /** Deep brand stop. Gradient end, hero bands. */
    deep: string;
    /** Third brand accent. Decorative only — never the sole carrier of meaning. */
    tertiary: string;
  }
  interface PaletteOptions {
    rule?: string;
    soft?: string;
    carbon?: string;
    deep?: string;
    tertiary?: string;
  }
}

export interface ThemeTokens {
  // Brand palette
  primary: string;
  primaryLight: string;
  primaryDark: string;
  primaryVeryLight: string;
  primaryVeryDark: string;

  // Extended brand — the RadNet DLS brand contract is five fields, not one ramp.
  // Optional: a theme that doesn't define them simply doesn't get them.
  secondary?: string;
  tertiary?: string;
  deep?: string;
  /** Authored CSS gradient. May be oklch()/color-mix() — used raw, never computed on. */
  gradient?: string;

  // Status palette
  error: string;
  warning: string;
  success: string;
  info: string;

  // Surface palette
  backgroundDefault: string;
  backgroundPaper: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  divider: string;
  cardBorder: string;
  scrollbar: string;
  /** Dark neutral for inverted surfaces. */
  carbon?: string;
  /** Tonal wash background. */
  soft?: string;
  /** Stronger divider for section rules. */
  rule?: string;

  // Dark mode. Semantic colors are optional mode-specific overrides because the same hue often
  // needs a lighter luminance to retain contrast against dark surfaces.
  darkError?: string;
  darkWarning?: string;
  darkSuccess?: string;
  darkInfo?: string;
  darkPrimary?: string;
  darkPrimaryLight?: string;
  darkPrimaryDark?: string;
  darkPrimaryVeryLight?: string;
  darkBackgroundDefault?: string;
  darkBackgroundPaper?: string;
  darkTextPrimary?: string;
  darkTextSecondary?: string;
  darkDivider?: string;
  darkCardBorder?: string;

  // Misc
  appBarShadow: string;
  font: string;
  /** Display face for headings. Falls back to `font` when unset. */
  fontDisplay?: string;
  radius: number;
  /** Card corner radius. Falls back to radius * 2.5 when unset. */
  radiusCard?: number;
  /** Button corner radius. Falls back to `radius` when unset. 999 = pill. */
  radiusButton?: number;

  /** Named component style pack from the theme base (T3). Defaults to 'standard' = no change. */
  stylePack?: string;
  /** `comfortable` | `compact` — drives default control sizes. */
  density?: string;
  /** `flat` | `raised` — border-bounded vs shadow-lifted surfaces. */
  elevation?: string;
  /** `solid` | `gradient` | `transparent` — app-bar treatment. */
  appBarStyle?: string;
  /** MUI typography variants, verbatim from the theme base. */
  typography?: Record<string, unknown>;
  /** MUI easing + duration, verbatim from the theme base. Durations in ms. */
  motion?: { easing?: Record<string, string>; duration?: Record<string, number> };
  /** Registry-validated component rules from the theme base (T2). */
  styleRules?: Record<string, unknown>;
}

/** Which token set a build should use. Resolved from the theme's dark-mode policy. */
export type ThemeMode = 'light' | 'dark';

const SHARED_FONT = "'Roboto', 'Helvetica', 'Arial', sans-serif";

const DEFAULT_THEME_TOKENS: ThemeTokens = {
  // Brand palette
  primary: '#a10693',
  primaryLight: '#f95aec',
  primaryDark: '#61045a',
  primaryVeryLight: '#fdbdf7',
  primaryVeryDark: '#31022d',

  // Status palette
  error: '#ff071c',
  warning: '#ff5900',
  success: '#24c246',
  info: '#187ae2',

  // Surface palette
  backgroundDefault: '#f7f8fa',
  backgroundPaper:   '#ffffff',
  textPrimary:       '#111827',
  textSecondary:     '#6b7280',
  textDisabled:      '#9ca3af',
  divider:           '#f3f4f6',
  cardBorder:        '#e5e7eb',
  scrollbar:         '#d1d5db',

  // Misc
  appBarShadow: '0 1px 3px 0 rgba(0,0,0,0.07)',
  font: SHARED_FONT,
  radius: 4,
};

/** Narrow an unknown typography entry to a spreadable object. */
const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/**
 * Pick the token set for the effective mode.
 *
 * Dark tokens fall back to their light counterpart rather than to a hardcoded dark palette:
 * a theme that has not authored dark values should look like itself with `palette.mode: 'dark'`
 * applied, not like some other brand.
 */
function forMode(tokens: ThemeTokens, mode: ThemeMode) {
  if (mode === 'light') {
    return {
      primary: tokens.primary,
      primaryLight: tokens.primaryLight,
      primaryDark: tokens.primaryDark,
      primaryVeryLight: tokens.primaryVeryLight,
      backgroundDefault: tokens.backgroundDefault,
      backgroundPaper: tokens.backgroundPaper,
      textPrimary: tokens.textPrimary,
      textSecondary: tokens.textSecondary,
      divider: tokens.divider,
      cardBorder: tokens.cardBorder,
      error: tokens.error,
      warning: tokens.warning,
      success: tokens.success,
      info: tokens.info,
    };
  }
  return {
    primary: tokens.darkPrimary ?? tokens.primary,
    primaryLight: tokens.darkPrimaryLight ?? tokens.primaryLight,
    primaryDark: tokens.darkPrimaryDark ?? tokens.primaryDark,
    primaryVeryLight: tokens.darkPrimaryVeryLight ?? tokens.primaryVeryLight,
    backgroundDefault: tokens.darkBackgroundDefault ?? '#121212',
    backgroundPaper: tokens.darkBackgroundPaper ?? '#1E1E1E',
    textPrimary: tokens.darkTextPrimary ?? 'rgba(255,255,255,0.90)',
    textSecondary: tokens.darkTextSecondary ?? 'rgba(255,255,255,0.70)',
    divider: tokens.darkDivider ?? 'rgba(255,255,255,0.12)',
    cardBorder: tokens.darkCardBorder ?? 'rgba(255,255,255,0.12)',
    error: tokens.darkError ?? tokens.error,
    warning: tokens.darkWarning ?? tokens.warning,
    success: tokens.darkSuccess ?? tokens.success,
    info: tokens.darkInfo ?? tokens.info,
  };
}

/** Build an MUI theme from tokens + direction + mode */
function buildThemeOptions(
  tokens: ThemeTokens,
  direction: 'ltr' | 'rtl',
  mode: ThemeMode = 'light',
): ThemeOptions {
  const c = forMode(tokens, mode);
  const cardRadius = tokens.radiusCard ?? tokens.radius * 2.5;
  const buttonRadius = tokens.radiusButton ?? tokens.radius;

  // Phase A — the tokens that previously landed nowhere. Each falls back to something already
  // in the theme rather than to a hardcoded neutral, so an unauthored theme is unchanged and a
  // brand-authored one is honoured. Dark mode inverts the alpha direction: a light tonal wash
  // reads as a dark one, so deriving from divider/text keeps them mode-correct for free.
  const inverse = mode === 'dark';
  const rule = tokens.rule ?? c.divider;
  const soft = tokens.soft ?? (inverse ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)');
  const carbon = tokens.carbon ?? (inverse ? '#000000' : '#121212');
  const deep = tokens.deep ?? c.primaryDark;
  const tertiary = tokens.tertiary ?? tokens.secondary ?? c.primary;

  // Structural knobs the server has been emitting and the client ignoring.
  const compact = tokens.density === 'compact';
  const raised = tokens.elevation === 'raised';
  const appBar = tokens.appBarStyle ?? 'solid';
  const brandGradient = tokens.gradient ?? `linear-gradient(135deg, ${c.primary}, ${deep})`;
  const appBarBackground =
    appBar === 'gradient' ? brandGradient
    : appBar === 'transparent' ? 'transparent'
    : undefined;

  return {
    direction,
    palette: {
      mode,
      // 4.5 not MUI's default of 3. Several authored brand primaries land between 3:1 and
      // 4.5:1 against white (Community Radiology #00a97c is 3.01:1), and at the default
      // threshold MUI picks a white label on them — legible as large text, non-compliant at
      // body size (WCAG 2.2 SC 1.4.3). At 4.5 MUI flips those to a dark label automatically,
      // and every brand that already passed is unaffected.
      contrastThreshold: 4.5,
      primary: {
        main: c.primary,
        light: c.primaryLight,
        dark: c.primaryDark,
      },
      ...(tokens.secondary ? { secondary: { main: tokens.secondary } } : {}),
      error: { main: c.error },
      warning: { main: c.warning },
      success: { main: c.success },
      info: { main: c.info },
      background: {
        default: c.backgroundDefault,
        paper: c.backgroundPaper,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
        disabled: tokens.textDisabled,
      },
      divider: c.divider,
      rule,
      soft,
      carbon,
      deep,
      tertiary,
    },
    typography: {
      fontFamily: tokens.font,
      fontSize: 14,
      h1: { fontWeight: 600 },
      h2: { fontWeight: 600 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 500 },
      // The base's authored type scale, applied last so it wins over the defaults above.
      // Spread rather than replaced: a base that only defines h1/h2 keeps everything else.
      ...(tokens.typography ?? {}),
      // A display face is a separate licensing decision from the body face, so it is applied
      // only to the heading variants and only when the base actually declares one.
      ...(tokens.fontDisplay
        ? {
            h1: { fontWeight: 600, ...(asRecord(tokens.typography?.h1)), fontFamily: tokens.fontDisplay },
            h2: { fontWeight: 600, ...(asRecord(tokens.typography?.h2)), fontFamily: tokens.fontDisplay },
            h3: { fontWeight: 600, ...(asRecord(tokens.typography?.h3)), fontFamily: tokens.fontDisplay },
          }
        : {}),
    },
    ...(tokens.motion
      ? {
          transitions: {
            ...(tokens.motion.easing ? { easing: tokens.motion.easing } : {}),
            ...(tokens.motion.duration ? { duration: tokens.motion.duration } : {}),
          },
        }
      : {}),
    shape: {
      borderRadius: tokens.radius,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: buttonRadius,
            textTransform: 'none',
            fontWeight: 500,
          },
        },
        // MUI v9 removed the per-variant slot keys (`containedPrimary` et al.) from
        // styleOverrides. `variants` with a props matcher is the replacement and is
        // equivalent — it targets variant="contained" color="primary" only.
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              // An authored brand gradient wins over the computed one. The DLS ships per-brand
              // oklch gradients that cannot be reproduced by interpolating two hex stops.
              background:
                tokens.gradient ?? `linear-gradient(135deg, ${c.primary}, ${c.primaryDark})`,
              '&:hover': tokens.gradient
                ? { filter: 'brightness(0.92)' }
                : {
                    background: `linear-gradient(135deg, ${c.primaryDark}, ${tokens.primaryVeryDark})`,
                  },
            },
          },
        ],
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${c.cardBorder}`,
            borderRadius: cardRadius,
            // `elevation: raised` swaps the hairline shadow for a real lift; `flat` keeps the
            // border as the only boundary, which is also the high-contrast-mode safe choice.
            boxShadow: raised
              ? '0 4px 16px -6px rgba(0,0,0,0.18), 0 1px 3px 0 rgba(0,0,0,0.06)'
              : '0 1px 2px 0 rgba(0,0,0,0.05)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: cardRadius,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: compact ? 'small' : 'medium',
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              // `rule` is the DLS's stronger-than-divider border. Falls back to divider, so
              // a theme that does not author it looks exactly as before.
              '& fieldset': { borderColor: rule },
              '&:hover fieldset': { borderColor: c.textPrimary },
              '&.Mui-focused fieldset': {
                borderColor: c.primary,
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: c.divider },
        },
        variants: [
          // Opt-in stronger rule for section breaks: <Divider variant="rule" />.
          { props: { variant: 'rule' }, style: { borderColor: rule, borderBottomWidth: 2 } },
        ],
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: rule },
          // The tonal wash the DLS uses for column headers.
          head: { backgroundColor: soft, fontWeight: 600 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: carbon },
          arrow: { color: carbon },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: cardRadius,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radiusButton ?? tokens.radius * 1.5,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            // The sidebar edge belongs to the THEME, not to AppLayout. It used to be a
            // hardcoded '1px solid #f0f0f0' in the component, which overrode the theme's
            // 'none' and could not follow a brand — wrong on any dark palette.
            //
            // `divider` is the right token: a drawer edge separates regions, it does not
            // identify a control, so SC 1.4.11's 3:1 does not apply. A base that authors
            // `Divider` (the DLS uses --line at 0.08) changes this automatically, and a style
            // pack or rule can override it without touching the component.
            //
            // borderInlineEnd, not borderRight, so the edge lands on the correct side in
            // Arabic RTL — where the drawer itself flips.
            borderInlineEnd: `1px solid ${c.divider}`,
            borderRight: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: appBar === 'transparent' ? 'none' : tokens.appBarShadow,
            ...(appBarBackground ? { backgroundImage: appBarBackground } : {}),
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: `${tokens.scrollbar} transparent`,
            '&::-webkit-scrollbar': {
              width: '5px',
              height: '5px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: tokens.scrollbar,
              borderRadius: '99px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: darken(tokens.scrollbar, 0.2),
            },
          },
          ':focus-visible': {
            outline: `2px solid ${c.primary}`,
            outlineOffset: '2px',
            borderRadius: '4px',
          },
        },
      },
    },
  };
}

/**
 * PPTheme* keys the server writes into configOverride. The server resolves the
 * preset selected by `Theme` plus any per-facility tweaks before responding,
 * so this module just reads the resolved values verbatim. Unknown keys are
 * ignored — any subset is safe.
 */
const THEME_OVERRIDE_KEYS = {
  // Brand palette
  primary: 'PPThemePrimary',
  primaryLight: 'PPThemePrimaryLight',
  primaryDark: 'PPThemePrimaryDark',
  primaryVeryLight: 'PPThemePrimaryVeryLight',
  primaryVeryDark: 'PPThemePrimaryVeryDark',
  // Status palette
  error: 'PPThemeError',
  warning: 'PPThemeWarning',
  success: 'PPThemeSuccess',
  info: 'PPThemeInfo',
  // Surface palette
  backgroundDefault: 'PPThemeBackgroundDefault',
  backgroundPaper:   'PPThemeBackgroundPaper',
  textPrimary:       'PPThemeTextPrimary',
  textSecondary:     'PPThemeTextSecondary',
  textDisabled:      'PPThemeTextDisabled',
  divider:           'PPThemeDivider',
  cardBorder:        'PPThemeCardBorder',
  scrollbar:         'PPThemeScrollbar',
  carbon:            'PPThemeCarbon',
  soft:              'PPThemeSoft',
  rule:              'PPThemeRule',
  // Extended brand (DLS five-field contract)
  secondary: 'PPThemeSecondary',
  tertiary:  'PPThemeTertiary',
  deep:      'PPThemeDeep',
  gradient:  'PPThemeGradient',
  // Dark mode
  darkPrimary:            'PPThemeDarkPrimary',
  darkPrimaryLight:       'PPThemeDarkPrimaryLight',
  darkPrimaryDark:        'PPThemeDarkPrimaryDark',
  darkPrimaryVeryLight:   'PPThemeDarkPrimaryVeryLight',
  darkError:              'PPThemeDarkError',
  darkWarning:            'PPThemeDarkWarning',
  darkSuccess:            'PPThemeDarkSuccess',
  darkInfo:               'PPThemeDarkInfo',
  darkBackgroundDefault:  'PPThemeDarkBackgroundDefault',
  darkBackgroundPaper:    'PPThemeDarkBackgroundPaper',
  darkTextPrimary:        'PPThemeDarkTextPrimary',
  darkTextSecondary:      'PPThemeDarkTextSecondary',
  darkDivider:            'PPThemeDarkDivider',
  darkCardBorder:         'PPThemeDarkCardBorder',
  // Misc
  appBarShadow: 'PPThemeAppBarShadow',
  font:         'PPThemeFont',
  fontDisplay:  'PPThemeFontDisplay',
  stylePack:    'PPThemeStylePack',
  density:      'PPThemeDensity',
  elevation:    'PPThemeElevation',
  appBarStyle:  'PPThemeAppBarStyle',
  radius:       'PPThemeRadius',
  radiusCard:   'PPThemeRadiusCard',
  radiusButton: 'PPThemeRadiusButton',
} as const;

/** Keys whose value is a number, not a string. */
const NUMERIC_TOKENS = new Set(['radius', 'radiusCard', 'radiusButton']);

/**
 * Nested documents arrive as JSON strings because configOverride is Record<string, string>.
 * Parsed defensively — a malformed bag must not stop the theme from building, or one bad
 * admin edit takes the portal down rather than just losing the type scale.
 */
function parseJsonToken<T>(raw: string | undefined): T | undefined {
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as T) : undefined;
  } catch {
    return undefined;
  }
}

/** The theme's dark-mode policy, as authored on the theme row. */
export type DarkModePolicy = 'light' | 'dark' | 'system';

export function darkModePolicyFromConfig(
  configOverride: Record<string, string> | null | undefined,
): DarkModePolicy {
  const raw = configOverride?.PPThemeDarkMode?.trim().toLowerCase();
  return raw === 'dark' || raw === 'system' ? raw : 'light';
}

/** Resolve the policy against the OS preference. Pure so it can be unit-tested. */
export function resolveThemeMode(policy: DarkModePolicy, prefersDark: boolean): ThemeMode {
  if (policy === 'system') return prefersDark ? 'dark' : 'light';
  return policy === 'dark' ? 'dark' : 'light';
}

/**
 * Returns the effective ThemeTokens for a site group, starting from the
 * built-in fallback and overlaying every PPTheme* value the server resolved.
 * Pass the result to createAppTheme.
 */
export function themeTokensFromConfig(
  configOverride: Record<string, string> | null | undefined
): ThemeTokens {
  if (!configOverride) return DEFAULT_THEME_TOKENS;

  const overrides: Partial<ThemeTokens> = {};
  for (const [tokenKey, configKey] of Object.entries(THEME_OVERRIDE_KEYS)) {
    const raw = configOverride[configKey];
    if (raw === undefined || raw === '') continue;
    if (NUMERIC_TOKENS.has(tokenKey)) {
      const n = Number(raw);
      if (!Number.isNaN(n)) (overrides as Record<string, number>)[tokenKey] = n;
    } else {
      (overrides as Record<string, string>)[tokenKey] = raw;
    }
  }

  // Nested bags are not in THEME_OVERRIDE_KEYS because they need parsing rather than a
  // straight copy.
  const typography = parseJsonToken<Record<string, unknown>>(configOverride.PPThemeTypography);
  const motion = parseJsonToken<ThemeTokens['motion']>(configOverride.PPThemeMotion);
  const styleRules = parseJsonToken<Record<string, unknown>>(configOverride.PPThemeStyleRules);
  if (typography) overrides.typography = typography;
  if (motion) overrides.motion = motion;
  if (styleRules) overrides.styleRules = styleRules;

  return { ...DEFAULT_THEME_TOKENS, ...overrides };
}

/** Returns the offline-fallback tokens (used before /Experience resolves). */
export function getTheme(): ThemeTokens {
  return DEFAULT_THEME_TOKENS;
}

/**
 * Create an MUI theme for the given direction, with optional fully-resolved tokens and mode.
 *
 * `mode` defaults to 'light' so every existing call site is unchanged — dark mode only engages
 * where a caller resolves the policy and passes it in.
 */
export function createAppTheme(
  direction: 'ltr' | 'rtl' = 'ltr',
  tokens?: ThemeTokens,
  mode: ThemeMode = 'light',
): Theme {
  const t = tokens ?? DEFAULT_THEME_TOKENS;
  const base = createTheme(buildThemeOptions(t, direction, mode));

  // Second pass so the style pack can read the RESOLVED theme — palette (including the derived
  // contrastText the 4.5 threshold produces), typography, spacing and transitions. A pack built
  // in one pass would have to duplicate all of that.
  //
  // 'standard' returns {}, so a theme with no pack — every DeepHealth facility today — comes out
  // of this byte-identical to the single-pass result.
  const packName: StylePackName = isStylePackName(t.stylePack) ? t.stylePack : 'standard';
  const pack = STYLE_PACKS[packName](base);
  const withPack = Object.keys(pack).length > 0 ? createTheme(base, { components: pack }) : base;

  // Third pass — registry-bounded rules from the base, applied LAST so an authored rule wins
  // over the code pack. That is the point: a pack is the reviewed default, a rule is the
  // per-base deviation that does not need a deploy.
  //
  // Resolved against `withPack` rather than `base` so a rule reading theme values sees whatever
  // the pack established. Every value is clamped or dropped by applyStyleRules, so an unknown
  // key or an out-of-range number degrades instead of breaking.
  const rules = applyStyleRules(t.styleRules, withPack);
  if (Object.keys(rules).length === 0) return withPack;

  return createTheme(withPack, { components: rules });
}
